// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ChatToolCalls.tsx
 * @input Uses React, StyleX, theme tokens
 * @output Exports ChatToolCalls component
 * @position Chat component — displays tool/function call invocations
 *
 * Single component that handles both individual and grouped tool calls.
 * Accepts an array of call data matching the shape LLM APIs already return.
 * Single call renders inline; multiple calls get a collapsible summary.
 *
 * Design rationale: every LLM API (Vercel AI SDK, Anthropic, OpenAI)
 * returns tool calls as an array on the message. One component with a
 * data prop matches that shape directly — no nested compound components
 * for builders to wire up.
 */
import React, { useState, useCallback, useId } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, typeScaleVars, typographyVars, fontWeightVars, radiusVars, durationVars, easeVars } from "../theme/tokens.stylex.js";
import { getKey, mergeProps } from "../utils/index.js";
import { Badge } from "../Badge/index.js";
import { Icon } from "../Icon/index.js";
import { Spinner } from "../Spinner/index.js";
import { themeProps } from "../utils/themeProps.js";

// =============================================================================
// Types
// =============================================================================
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
// =============================================================================
// Styles
// =============================================================================

const styles = {
  root: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    keoZOQ: "xtbrsbv",
    $$css: true
  },
  statusIcon: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    kzqmXN: "x1kky2od",
    kZKoxP: "xlup9mm",
    kaIpWk: "xjspbzw",
    $$css: true
  },
  nodePill: {
    kmuXW: "x2lah0s",
    $$css: true
  },
  colorPending: {
    kMwMTN: "xnbbluu",
    $$css: true
  },
  colorRunning: {
    kMwMTN: "xqwr325",
    $$css: true
  },
  colorComplete: {
    kMwMTN: "xtjic6",
    $$css: true
  },
  colorError: {
    kMwMTN: "xjt36v0",
    $$css: true
  }
};

// =============================================================================
// Status Icons
// =============================================================================

const STATUS_ICON_NAMES = {
  pending: 'clock',
  running: null,
  complete: 'check',
  error: 'close'
};
const STATUS_STYLES = {
  pending: styles.colorPending,
  running: styles.colorRunning,
  complete: styles.colorComplete,
  error: styles.colorError
};
function getToolCallKey(call) {
  return getKey(call.key, () => [call.name, call.status ?? 'complete', call.target ?? '', call.node ?? '', call.duration ?? '', call.additions?.toString() ?? '', call.deletions?.toString() ?? '', call.errorMessage ?? ''].join('\u001F'));
}

// =============================================================================
// Internal: single call row
// =============================================================================

function CallRow({
  call
}) {
  const status = call.status ?? 'complete';
  const hasDetail = call.resultDetail != null;
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const detailId = useId();
  const toggleDetail = hasDetail ? () => setIsDetailOpen(prev => !prev) : undefined;
  const row = /*#__PURE__*/_jsxs("div", {
    role: hasDetail ? 'button' : undefined,
    tabIndex: hasDetail ? 0 : undefined,
    "aria-expanded": hasDetail ? isDetailOpen : undefined,
    "aria-controls": hasDetail && isDetailOpen ? detailId : undefined,
    onClick: toggleDetail,
    onKeyDown: hasDetail ? e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleDetail?.();
      }
    } : undefined,
    ...{
      0: {
        className: "x78zum5 x6s0dn4 x1s4dlld xjwf9q1 x13f7esw"
      },
      1: {
        className: "x78zum5 x6s0dn4 x1s4dlld xjwf9q1 x13f7esw x1ypdohk xh6dtrn x7a5moj xtedp8i xe9uy6x"
      }
    }[!!hasDetail << 0],
    children: [/*#__PURE__*/_jsx("span", {
      title: status === 'error' ? call.errorMessage : undefined,
      ...stylex.props(styles.statusIcon, STATUS_STYLES[status]),
      children: status === 'running' ? /*#__PURE__*/_jsx(Spinner, {
        size: "sm",
        shade: "subtle"
      }) : status === 'pending' ? /*#__PURE__*/_jsx(Spinner, {
        size: "sm",
        shade: "subtle"
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("span", {
          ...{
            className: "x10l6tqk x10a8y8t x1pjcqnp xtwfq29 x1xyvc85"
          }
        }), /*#__PURE__*/_jsx("span", {
          ...{
            className: "x1n2onr6 x3nfvp2"
          },
          children: /*#__PURE__*/_jsx(Icon, {
            icon: STATUS_ICON_NAMES[status] ?? 'check',
            size: "xsm",
            color: "inherit"
          })
        })]
      })
    }), /*#__PURE__*/_jsx("span", {
      ...{
        className: "x141an7d x1ltkj2j x9m5x89 x1e4wzip xv1l7n4 xuxw1ft xb3r6kr xlyipyv xs83m0k x13n9wmd"
      },
      children: call.name
    }), call.node != null && /*#__PURE__*/_jsx(Badge, {
      label: call.node,
      variant: "neutral",
      xstyle: styles.nodePill
    }), call.target != null && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x141an7d x1ltkj2j x9ynric xnbbluu xuxw1ft xb3r6kr xlyipyv x1g3ib7 xeuugli"
      },
      children: call.target
    }), (call.additions != null || call.deletions != null || call.stats != null) && /*#__PURE__*/_jsxs("span", {
      ...{
        className: "x78zum5 x6s0dn4 xzye2dw x141an7d x1ltkj2j x9ynric xnbbluu x2lah0s"
      },
      children: [call.additions != null && /*#__PURE__*/_jsxs("span", {
        ...{
          className: "xtjic6"
        },
        children: ["+", call.additions]
      }), call.deletions != null && /*#__PURE__*/_jsxs("span", {
        ...{
          className: "xjt36v0"
        },
        children: ["-", call.deletions]
      }), call.stats]
    }), call.duration != null && status === 'complete' && /*#__PURE__*/_jsx("span", {
      ...{
        className: "x141an7d x1ltkj2j x9ynric xnbbluu xuxw1ft x2lah0s"
      },
      children: call.duration
    }), hasDetail && /*#__PURE__*/_jsx("span", {
      ...{
        0: {
          className: "x3nfvp2 x6s0dn4 xl56j7k x2lah0s x6jxa94 x1v9usgg xnbbluu x1ob6yzd xvc5jky"
        },
        1: {
          className: "x3nfvp2 x6s0dn4 xl56j7k x2lah0s x6jxa94 x1v9usgg xnbbluu x1ob6yzd xvc5jky x19jd1h0"
        }
      }[!!isDetailOpen << 0],
      children: /*#__PURE__*/_jsx(Icon, {
        icon: "chevronDown",
        size: "xsm",
        color: "inherit"
      })
    })]
  });
  if (!hasDetail) {
    return row;
  }
  return /*#__PURE__*/_jsxs("div", {
    children: [row, isDetailOpen && /*#__PURE__*/_jsx("div", {
      id: detailId,
      ...{
        className: "x1f43n9v x1wesfrj"
      },
      children: call.resultDetail
    })]
  });
}

// =============================================================================
// Component
// =============================================================================

/**
 * Displays tool/function call invocations from an LLM response.
 *
 * Accepts a `calls` array matching the shape LLM APIs return. Single call
 * renders inline without group chrome. Multiple calls get a collapsible
 * summary header.
 *
 * @example
 * ```
 * <ChatToolCalls
 *   calls={message.toolCalls.map(tc => ({
 *     name: tc.toolName,
 *     status: tc.state,
 *     duration: tc.duration,
 *   }))}
 *   renderDetail={(call) => (
 *     <CodeBlock code={call.args} language="json" />
 *   )}
 * />
 * ```
 */
export function ChatToolCalls(props) {
  const {
    calls,
    label: _customLabel,
    isExpanded: controlledExpanded,
    defaultIsExpanded,
    onExpandedChange,
    xstyle,
    className,
    style,
    ref,
    ...rest
  } = props;
  const autoDefault = defaultIsExpanded ?? false;
  const [internalExpanded, setInternalExpanded] = useState(autoDefault);
  const contentId = useId();
  const isControlled = controlledExpanded !== undefined;
  const isExpanded = isControlled ? controlledExpanded : internalExpanded;
  const toggle = useCallback(() => {
    const next = !isExpanded;
    if (!isControlled) {
      setInternalExpanded(next);
    }
    onExpandedChange?.(next);
  }, [isExpanded, isControlled, onExpandedChange]);
  if (calls.length === 0) {
    return null;
  }

  // Single call: render inline, no group chrome
  if (calls.length === 1) {
    return /*#__PURE__*/_jsx("div", {
      ref: ref,
      ...mergeProps(themeProps('chat-tool-calls'), stylex.props(styles.root, xstyle), className, style),
      ...rest,
      children: /*#__PURE__*/_jsx(CallRow, {
        call: calls[0]
      })
    });
  }

  // Multiple calls: latest call at surface with chevron to expand all
  const latestCall = calls[calls.length - 1];
  const latestStatus = latestCall.status ?? 'complete';
  return /*#__PURE__*/_jsxs("div", {
    ref: ref,
    ...mergeProps(themeProps('chat-tool-calls'), stylex.props(styles.root, xstyle), className, style),
    ...rest,
    children: [/*#__PURE__*/_jsxs("div", {
      role: "button",
      tabIndex: 0,
      "aria-expanded": isExpanded,
      "aria-controls": contentId,
      onClick: toggle,
      onKeyDown: e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle();
        }
      },
      ...{
        className: "x78zum5 x6s0dn4 x1s4dlld xjwf9q1 x13f7esw x1ypdohk"
      },
      children: [isExpanded ? /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("span", {
          ...{
            className: "x3nfvp2 x6s0dn4 xl56j7k x2lah0s x1kky2od xlup9mm xv1l7n4"
          },
          children: /*#__PURE__*/_jsx(Icon, {
            icon: "wrench",
            size: "sm",
            color: "inherit"
          })
        }), /*#__PURE__*/_jsxs("span", {
          ...{
            className: "x141an7d x1ltkj2j x9ynric x1e4wzip xv1l7n4"
          },
          children: [calls.length, " tool calls"]
        })]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("span", {
          ...stylex.props(styles.statusIcon, STATUS_STYLES[latestStatus]),
          children: latestStatus === 'running' ? /*#__PURE__*/_jsx(Spinner, {
            size: "sm",
            shade: "subtle"
          }) : latestStatus === 'pending' ? /*#__PURE__*/_jsx(Spinner, {
            size: "sm",
            shade: "subtle"
          }) : /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx("span", {
              ...{
                className: "x10l6tqk x10a8y8t x1pjcqnp xtwfq29 x1xyvc85"
              }
            }), /*#__PURE__*/_jsx("span", {
              ...{
                className: "x1n2onr6 x3nfvp2"
              },
              children: /*#__PURE__*/_jsx(Icon, {
                icon: STATUS_ICON_NAMES[latestStatus] ?? 'check',
                size: "xsm",
                color: "inherit"
              })
            })]
          })
        }), /*#__PURE__*/_jsx("span", {
          ...{
            className: "x141an7d x1ltkj2j x9m5x89 x1e4wzip xv1l7n4 xuxw1ft xb3r6kr xlyipyv xs83m0k x13n9wmd"
          },
          children: latestCall.name
        }), latestCall.target != null && /*#__PURE__*/_jsx("span", {
          ...{
            className: "x141an7d x1ltkj2j x9ynric xnbbluu xuxw1ft xb3r6kr xlyipyv x1g3ib7 xeuugli"
          },
          children: latestCall.target
        })]
      }), /*#__PURE__*/_jsx("span", {
        ...{
          className: "x3nfvp2 x6s0dn4 x1lsbc85 x141an7d x1ltkj2j x9ynric xnbbluu x2lah0s"
        },
        children: !isExpanded && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Icon, {
            icon: "wrench",
            size: "xsm",
            color: "inherit"
          }), calls.length]
        })
      }), /*#__PURE__*/_jsx("span", {
        ...{
          0: {
            className: "x3nfvp2 x6s0dn4 xl56j7k x2lah0s x6jxa94 x1v9usgg xnbbluu x1ob6yzd"
          },
          1: {
            className: "x3nfvp2 x6s0dn4 xl56j7k x2lah0s x6jxa94 x1v9usgg xnbbluu x1ob6yzd x19jd1h0"
          }
        }[!!isExpanded << 0],
        children: /*#__PURE__*/_jsx(Icon, {
          icon: "chevronDown",
          size: "xsm",
          color: "inherit"
        })
      })]
    }), /*#__PURE__*/_jsx("div", {
      id: contentId,
      ...{
        0: {
          className: "xrvj5dj xihq33y xb0j27v"
        },
        1: {
          className: "xrvj5dj xb0j27v x1tu4anv"
        }
      }[!!isExpanded << 0],
      children: /*#__PURE__*/_jsx("div", {
        ...{
          className: "xb3r6kr x2lwn1j"
        },
        children: /*#__PURE__*/_jsx("div", {
          ...{
            className: "x78zum5 xdt5ytf x7a5moj xtedp8i"
          },
          children: calls.map(call => /*#__PURE__*/_jsx(CallRow, {
            call: call
          }, getToolCallKey(call)))
        })
      })
    })]
  });
}
ChatToolCalls.displayName = 'ChatToolCalls';